import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Megaphone, X, ArrowRight, ExternalLink } from "lucide-react";
import api from "@/lib/api";

const URL_RE = /(https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)]|(?:^|\s)\/[a-z][\w\-/?=&.#]*)/gi;
const isInternal = (u) => u.startsWith("/") || (typeof window !== "undefined" && u.startsWith(window.location.origin));
const toPath = (u) => (u.startsWith("/") ? u : u.slice(window.location.origin.length) || "/");

/** Turns pasted URLs (and in-app paths like /watch) inside the body into tappable links. */
const Linkify = ({ text }) => {
  const parts = String(text || "").split(URL_RE);
  return parts.map((p, i) => {
    if (!p) return null;
    if (!new RegExp(URL_RE.source, "i").test(p)) return <span key={i}>{p}</span>;
    const lead = p.match(/^\s*/)[0];
    const u = p.trim();
    const cls = "underline decoration-amber-300/70 underline-offset-2 text-amber-200 hover:text-amber-100 break-all";
    return (
      <span key={i}>{lead}{isInternal(u)
        ? <Link to={toPath(u)} className={cls} data-testid="announcement-inline-link">{u}</Link>
        : <a href={u} target="_blank" rel="noreferrer" className={cls} data-testid="announcement-inline-link">{u}</a>}</span>
    );
  });
};

const BannerLink = ({ url, label }) => {
  if (!url) return null;
  const cls = "mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-400 text-slate-900 text-xs font-semibold px-3 py-1.5 hover:bg-amber-300 transition-colors";
  const text = label || "Open";
  return isInternal(url)
    ? <Link to={toPath(url)} className={cls} data-testid="announcement-link">{text} <ArrowRight className="w-3.5 h-3.5" /></Link>
    : <a href={url} target="_blank" rel="noreferrer" className={cls} data-testid="announcement-link">{text} <ExternalLink className="w-3.5 h-3.5" /></a>;
};

/** Dismissible platform announcements from the Kingly Back Office (one at a time, newest first). */
export const AnnouncementBanner = ({ className = "" }) => {
  const qc = useQueryClient();
  const { data = [] } = useQuery({
    queryKey: ["announcements"],
    queryFn: async () => (await api.get("/announcements")).data,
    staleTime: 60_000,
  });
  const dismiss = useMutation({
    mutationFn: async (id) => (await api.post(`/announcements/${id}/dismiss`)).data,
    onMutate: (id) => qc.setQueryData(["announcements"], (old = []) => old.filter((a) => a.id !== id)),
  });
  const a = data[0];
  if (!a) return null;
  return (
    <div className={className} data-testid="announcement-banner">
      <div className="flex items-start gap-3 bg-slate-900 text-white rounded-xl px-4 py-3 shadow-lg border border-amber-400/40">
        <Megaphone className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold" data-testid="announcement-title">{a.title}</div>
          <div className="text-sm text-slate-200 mt-0.5 whitespace-pre-line" data-testid="announcement-body"><Linkify text={a.body} /></div>
          {a.sticker_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${a.sticker_url}`} alt="" draggable={false} className="mt-2 h-24 w-24 object-contain" data-testid="announcement-sticker" />}
          <BannerLink url={a.link_url} label={a.link_label} />
          {data.length > 1 && <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">+{data.length - 1} more</div>}
        </div>
        <button onClick={() => dismiss.mutate(a.id)} aria-label="Dismiss" data-testid="announcement-dismiss"
          className="p-1 rounded-md text-slate-300 hover:text-white hover:bg-white/10">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
